import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Badge,
    Modal, Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, InputGroup, Input
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Eye, Users, Briefcase } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

interface EmployeeData {
    id: number;
    name: string;
    email: string;
    phone: string;
    designation: string;
    status: string;
    address?: string;
    joining_date?: string;
    profile_image?: string | null;
    role: { name: string };
}

export default function ViewEmployees() {
    const [data, setData] = useState<EmployeeData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewModal, setViewModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);
    const [activeTab, setActiveTab] = useState('1');
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, refresh, currentPage, perPage, sortBy, sortDir]);

    const fetchData = async (page: number, size: number, sort: string, direction: string, search: string) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`employee/get_all?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search
            });
            setData(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            toast.error("Failed to load employees");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || column.selector.toString().split('.').pop()); // Fallback to selector string naming
        setSortDir(sortDirection);
    };

    const handleAdd = () => navigate(`${process.env.PUBLIC_URL}/add_employee`);
    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_employee`, { state: { id } });

    const handleView = (employee: EmployeeData) => {
        setSelectedEmployee(employee);
        setActiveTab('1');
        setViewModal(true);
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`employee/delete`, { id });
                    toast.success("Employee deleted successfully");
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete employee");
                }
            }
        });
    };

    const columns: any[] = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: EmployeeData, rowIndex?: number) => (currentPage - 1) * perPage + (rowIndex || 0) + 1,
            width: '100px',
        },
        { name: <div><span className="fw-bold">Name</span></div>, selector: (row: EmployeeData) => row.name, sortable: true, sortField: "name", wrap: true },
        { name: <div><span className="fw-bold">Email</span></div>, selector: (row: EmployeeData) => row.email, sortable: true, sortField: "email", wrap: true },
        { name: <div><span className="fw-bold">Designation</span></div>, selector: (row: EmployeeData) => row.designation || "N/A", sortable: true, sortField: "designation", wrap: true },
        { name: <div><span className="fw-bold">Role</span></div>, selector: (row: EmployeeData) => row.role?.name || "No Role", sortable: true, sortField: "role", wrap: true },
        {
            name: <div><span className="fw-bold">Status</span></div>,
            sortable: true,
            sortField: "status",
            cell: (row: EmployeeData) => (
                <Badge color={row.status === 'Active' ? 'success' : 'danger'}>{row.status}</Badge>
            ),
        },
        {
            name: <div><span className="fw-bold">Actions</span></div>,
            cell: (row: EmployeeData) => (
                <div className="d-flex gap-2">
                    <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Employee">
                        <Eye size={14} />
                    </Button>
                    {hasPermission('employee-management.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)}><Edit size={14} /></Button>
                    )}
                    {hasPermission('employee-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)}><Trash2 size={14} /></Button>
                    )}
                </div>
            ),
        },
    ];
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };


    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Employee Management" parent="User Management" title="Employees" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <H3 className="text-primary mb-0">Employee Directory</H3>
                            {hasPermission('employee-management.add') && (
                                <Btn color="primary" onClick={handleAdd} className="d-flex align-items-center gap-1">
                                    <Plus size={16} /> Add Employee
                                </Btn>
                            )}
                        </div>

                        <Row className="mb-3 justify-content-end">
                            <Col md={4} lg={3}>
                                <InputGroup className="shadow-sm border-0">
                                    <Input
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control-sm"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>

                        <DataTable
                            columns={columns}
                            data={data}
                            progressPending={loading}
                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            sortServer
                            onSort={handleSort}
                            highlightOnHover
                            customStyles={customStyles}

                            noDataComponent={
                                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                    <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                        <CardBody>
                                            <h6 className="fw-semibold mb-1">No Employee Found</h6>
                                            <small className="text-muted">You haven't created any employee yet.</small>
                                        </CardBody>
                                    </Card>
                                </div>
                            }
                        />
                    </CardBody>
                </Card>
            </Container>

            {/* Premium Employee View Modal */}
            <Modal
                isOpen={viewModal}
                toggle={() => setViewModal(!viewModal)}
                size="md"
                centered
            >
                {selectedEmployee && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e6ec8 0%, #0d4fa0 100%)',
                        padding: '1.5rem',
                        position: 'relative',
                        width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={() => setViewModal(false)}
                            style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', opacity: 0.9, }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0
                            }}>
                                <Users size={28} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.4rem', margin: 0, marginBottom: '0.25rem' }}>
                                    {selectedEmployee.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '0.75rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>{selectedEmployee.designation || 'Employee'}</span>
                                    <span>•</span>
                                    <span>{selectedEmployee.role?.name || 'No Role'}</span>
                                </div>
                            </div>
                            <span style={{
                                backgroundColor: selectedEmployee.status === 'Active' ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
                                color: 'white',
                                padding: '0.4rem 1rem',
                                borderRadius: '20px',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                border: `1px solid ${selectedEmployee.status === 'Active' ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'}`,
                                whiteSpace: 'nowrap'
                            }}>
                                {selectedEmployee.status}
                            </span>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    <div className="premium-modal-tabs">
                        <Nav tabs style={{ marginTop: '0.5rem' }}>
                            <NavItem>
                                <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')} style={{ cursor: 'pointer' }}>
                                    <Users size={14} /> Personal Info
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')} style={{ cursor: 'pointer' }}>
                                    <Briefcase size={14} /> Work Details
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </div>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '260px' }}>
                        {selectedEmployee && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-3">
                                            {/* Profile Image */}
                                            {selectedEmployee.profile_image && (
                                                <div className="col-12 text-center mb-2">
                                                    <img
                                                        src={`${mediaUrl}${selectedEmployee.profile_image}`}
                                                        alt={selectedEmployee.name}
                                                        style={{
                                                            width: '80px', height: '80px',
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                            border: '3px solid #1e6ec8',
                                                            boxShadow: '0 2px 8px rgba(30,110,200,0.2)'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <div className="col-12">
                                                <label className="text-muted small fw-bold text-uppercase">Full Name</label>
                                                <div className="fw-semibold fs-6">{selectedEmployee.name}</div>
                                            </div>
                                            <div className="col-md-6" style={{ minWidth: 0 }}>
                                                <label className="text-muted small fw-bold text-uppercase">Email</label>
                                                <div className="fw-semibold text-primary" style={{ wordBreak: 'break-all', fontSize: '13px' }}>{selectedEmployee.email}</div>
                                            </div>
                                            <div className="col-md-6" style={{ minWidth: 0 }}>
                                                <label className="text-muted small fw-bold text-uppercase">Phone</label>
                                                <div className="fw-semibold" style={{ wordBreak: 'break-all', fontSize: '13px' }}>{selectedEmployee.phone || '—'}</div>
                                            </div>
                                            {selectedEmployee.address && (
                                                <div className="col-12">
                                                    <label className="text-muted small fw-bold text-uppercase">Address</label>
                                                    <div className="p-3 bg-light rounded mt-1 text-dark">{selectedEmployee.address}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase">Designation</label>
                                                <div className="fw-semibold">{selectedEmployee.designation || '—'}</div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase">Role</label>
                                                <div className="fw-semibold text-primary">{selectedEmployee.role?.name || 'No Role'}</div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase">Status</label>
                                                <div className="mt-1">
                                                    <Badge color={selectedEmployee.status === 'Active' ? 'success' : 'danger'} pill style={{ fontSize: '12px', padding: '5px 12px' }}>
                                                        {selectedEmployee.status}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="text-muted small fw-bold text-uppercase">Joining Date</label>
                                                <div className="fw-semibold">{selectedEmployee.joining_date || '—'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>

            <ToastContainer />
        </div>
    );
}
